import {
  Autocomplete,
  Box,
  Chip,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useState } from "react";

import defaultStyles from "../TextField/styles";

interface StyledAutocompleteProps {
  label?: string;
  required?: boolean;
  completed?: boolean;
  styles?: object;
  value?: any;
  getOptionLabel?: (option: any) => string;
  renderInput?: (params: any) => React.ReactNode;
  renderTags?: (value: any, getTagProps: any) => React.ReactNode;
  ChipProps?: object;
  isActive?: boolean;
  error?: string;
  [key: string]: any;
}

export default function StyledAutocomplete({
  label = "",
  required = false,
  completed = false,
  styles = {},
  value,
  getOptionLabel,
  renderInput,
  renderTags,
  ChipProps,
  isActive = false,
  error,
  ...rest
}: StyledAutocompleteProps) {
  const [filled, setFilled] = useState(completed);

  const getDisplayValue = (): string => {
    if (!value) return "Incomplete";
    if (getOptionLabel) return getOptionLabel(value);
    return Array.isArray(value) ? value.join(", ") : String(value);
  };

  const handleContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node) && completed) {
      setFilled(true);
    }
  };

  return (
    <>
      {!filled && (
        <Box onBlur={handleContainerBlur}>
          {label && (
            <Typography gutterBottom>
              {label} {required ? "*" : ""}
            </Typography>
          )}
          <Autocomplete
            {...rest}
            value={value}
            getOptionLabel={getOptionLabel}
            renderInput={
              renderInput ||
              ((params) => (
                <TextField
                  {...params}
                  sx={[defaultStyles.root, styles]}
                  placeholder="Select all that apply"
                />
              ))
            }
            renderTags={
              renderTags ||
              ((value: any, getTagProps: any) =>
                value.map((option: any, index: number) => (
                  <Chip
                    label={getOptionLabel ? getOptionLabel(option) : option}
                    {...getTagProps({ index })}
                    key={index}
                    color="primary"
                    {...ChipProps}
                  />
                )))
            }
            options={rest.options || []}
            sx={{
              "& fieldset.MuiOutlinedInput-notchedOutline": {
                borderRadius: 4,
                borderColor: isActive ? "green.main" : grey[300],
                borderWidth: isActive ? 2 : 1,
              },
            }}
          />
        </Box>
      )}

      {filled && (
        <Grid
          alignItems="center"
          gap={2}
          container
          onClick={() => setFilled(false)}
          sx={{
            border: `1px solid ${grey[300]}`,
            borderRadius: "16px",
            p: 2,
            width: "100%",
            mb: 2,
            cursor: "pointer",
          }}
        >
          <Grid item xs>
            {label && (
              <Typography gutterBottom sx={{ color: "#151F16", fontSize: 12 }}>
                {label} {required ? "*" : ""}
              </Typography>
            )}
            <Typography
              variant="body1"
              sx={{ color: "black", fontWeight: 500 }}
            >
              {getDisplayValue()}
            </Typography>
          </Grid>

          <Grid item xs="auto">
            <Box
              component="i"
              className="fa-regular fa-check"
              sx={{ color: "green.main" }}
            />
          </Grid>
        </Grid>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: -1 }}>
          {error}
        </Typography>
      )}
    </>
  );
}
